import { AggregateBy } from '@internal/backstage-plugin-common-library-react';
import { DateTime } from 'luxon';

import {
  CategoryData,
  CategoryPeriod,
  CategoryPeriodValue,
  ScorecardItem,
} from '../types';

export const toStandardISO = (date: DateTime) => {
  return `${
    date
      .startOf('second')
      .toISO({ includeOffset: false, suppressMilliseconds: true }) as string
  }Z`;
};

export const getHeaders = (token: string | undefined): HeadersInit => {
  const headers: HeadersInit = new Headers();
  headers.set('content-type', 'application/json');
  headers.set('Authorization', `Bearer ${token}`);
  return headers;
};

function* colorGenerator() {
  const colors = [
    '#883eb1',
    '#b1623eff',
    '#a73b37ff',
    '#c7cf32ff',
    '#1a4975ff',
    '#2e7d32',
    '#f57c00',
    '#d32f2f',
    '#7b1fa2',
    '#0288d1',
  ];

  let index = 0;
  while (true) {
    yield colors[index % colors.length];
    index++;
  }
}

const colorGen = colorGenerator();

export const getRandomColor = (): string => {
  return colorGen.next().value as string;
};

type CheckStatus = 'passed' | 'failed' | 'N/A';

type RepoCheck<Meta extends object = {}> = {
  check_status: CheckStatus;
  team_id?: string;
  value?: number;
} & Meta;

type Repository = {
  repository_name: string;
  pushed_at?: string;
  checks: Record<string, RepoCheck>;
};

type TeamNode = {
  team_id: string;
  repositories?: Repository[];
  children: TeamNode[];
  checks?: { check_status: string }[];
};

type CheckStatusSummary = {
  checkName: string;
  passed: boolean;
  label: string;
  description: string;
  checkDetails: { check_status: string; [key: string]: any }[];
};

export const getTeamBasedChecksStatus = (
  teamNode: TeamNode,
  check_structure: any,
): CheckStatusSummary[] => {
  if (!teamNode) {
    return [];
  }
  const checkNames = Object.keys(check_structure);

  const collectAllTeams = (node: TeamNode): TeamNode[] => {
    let teams = [node];
    for (const child of node.children || []) {
      teams = teams.concat(collectAllTeams(child));
    }
    return teams;
  };

  const allTeams = collectAllTeams(teamNode);

  return checkNames.map(checkName => {
    const matchesCheck = (check: any) =>
      check !== null &&
      check !== undefined &&
      (check.check_name
        ? check.check_name === checkName
        : checkNames.length === 1);

    const statuses = allTeams
      .map(team =>
        (team.checks as any[])
          ?.filter(matchesCheck)
          .map(check => check?.check_status),
      )
      .flat()
      .filter(status => status !== undefined);

    const passed =
      statuses.length > 0 && !statuses.some(status => status === 'failed');

    const currentCheckStructure =
      check_structure[checkName as keyof typeof check_structure];

    const checkDetails = allTeams
      .filter(team =>
        (team.checks as any[])?.some(
          check => check?.check_status === 'failed' && matchesCheck(check),
        ),
      )
      .map(team => ({
        check_status: 'failed',
        team_name: team.team_id,
        failing_resources:
          (team.checks as any[])
            ?.filter(
              check => check?.check_status === 'failed' && matchesCheck(check),
            )
            .map(check => ({ ...check, team_name: team.team_id })) ?? [],
      }))
      .flatMap(cd => cd.failing_resources);

    return {
      checkName,
      passed,
      label: currentCheckStructure.display_name,
      description: currentCheckStructure.description,
      checkDetails,
    };
  });
};

export const getScorecardItems = (
  categoriesData: CategoryData[],
  aggregation?: AggregateBy,
): ScorecardItem[] | null => {
  if (!categoriesData?.length) {
    return null;
  }

  const validCategoriesData = categoriesData.filter(categoryData =>
    Array.isArray((categoryData?.data as any)?.periods),
  );

  if (!validCategoriesData.length) {
    return null;
  }

  const getFormattedDate = (isoDate: string) => {
    if (aggregation === 'week') {
      const date = DateTime.fromISO(isoDate);
      const { weekNumber, year } = date;
      return `W${weekNumber}-${year} (${DateTime.fromISO(
        isoDate,
      ).toLocaleString({
        month: 'short',
        day: 'numeric',
      })})`;
    } else if (aggregation === 'month') {
      return DateTime.fromISO(isoDate).toLocaleString({
        month: 'long',
        year: 'numeric',
      });
    } else if (aggregation === 'quarter') {
      const date = DateTime.fromISO(isoDate);
      const quarterNumber = date.quarter;
      const year = date.year;
      return `${year}-Q${quarterNumber} (${date.toLocaleString({
        month: 'short',
      })})`;
    }

    return DateTime.fromISO(isoDate).toLocaleString({
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return validCategoriesData?.map((categoryData: CategoryData) => {
    return {
      checked: true,
      color: getRandomColor(),
      label: categoryData.category.trim(),
      valueOverTime: categoryData.data.periods.map((period: CategoryPeriod) => {
        const raw = period.value.aggregated_category_score_percentage;
        const value =
          raw === -1 ? null : Math.round(Math.max(raw, 0) * 100 * 100) / 100;
        return {
          date: getFormattedDate(period.start),
          value,
        };
      }),
    };
  });
};

const denominatorDividendDifference = (value: CategoryPeriodValue) =>
  value.aggregated_category_score_proportion_denominator === -1
    ? -1
    : value.aggregated_category_score_proportion_denominator -
      value.aggregated_category_score_proportion_dividend;

const aggregatedValue = (value: CategoryPeriodValue) => {
  if (value.aggregated_category_score_percentage === -1) {
    return value.aggregated_category_score_percentage;
  }

  const percentage =
    Math.max(value.aggregated_category_score_percentage, 0) * 100;
  const rounded = Math.round(percentage * 100) / 100;
  return `${rounded}%`;
};

const dummyValue = () => -1;

const KPIValueTransform = {
  Velocity: dummyValue,
  'End of Life': denominatorDividendDifference,
  'Privileged Identity Management': aggregatedValue,
  'Key Rotation': aggregatedValue,
  'Agent Coverage': aggregatedValue,
  'Public IP': denominatorDividendDifference,
  'Criticality Set': denominatorDividendDifference,
  'Blackduck Coverage': aggregatedValue,
  'Veracode Coverage': aggregatedValue,
  'Repository Ownership': dummyValue,
  'Investment Category': aggregatedValue,
};

export const getKPIItems = (
  categoriesData: CategoryData[],
): ScorecardItem[] | null => {
  if (!categoriesData?.length) {
    return null;
  }

  const validCategoriesData = categoriesData.filter(categoryData =>
    Array.isArray((categoryData?.data as any)?.periods),
  );

  if (!validCategoriesData.length) {
    return null;
  }

  return validCategoriesData?.map((categoryData: CategoryData) => {
    return {
      checked: true,
      color: getRandomColor(),
      label: categoryData.category.trim(),
      valueOverTime: categoryData.data.periods.map((period: CategoryPeriod) => {
        return {
          date: period.start,
          value: (
            KPIValueTransform[
              categoryData.category as keyof typeof KPIValueTransform
            ] ?? aggregatedValue
          )(period.value),
        };
      }),
    };
  });
};

export const getRepoBasedChecksStatus = (
  teamNode: TeamNode,
  check_structure: any,
): CheckStatusSummary[] => {
  const checkNames = Object.keys(check_structure);

  const collectAllRepos = (
    node: TeamNode,
  ): (Repository & { team_id: string })[] => {
    let repos = (node.repositories || []).map(r => ({
      ...r,
      team_id: node.team_id,
    }));
    for (const child of node.children || []) {
      repos = repos.concat(collectAllRepos(child));
    }
    return repos;
  };

  const allRepos = collectAllRepos(teamNode);

  return checkNames.map(checkName => {
    const statuses = allRepos
      .map(repo => repo.checks?.[checkName]?.check_status)
      .filter(status => status !== undefined);

    const allPassedOrNA =
      statuses.length > 0 &&
      statuses.every(status => status === 'passed' || status === 'N/A');

    const currentCheckStructure =
      check_structure[checkName as keyof typeof check_structure];

    const checkDetails = allRepos
      .filter(repo => repo.checks?.[checkName]?.check_status === 'failed')
      .map(repo => ({
        ...repo.checks[checkName],
        check_status: 'failed' as const,
        repository_name: repo.repository_name,
        team_id: repo.team_id,
        check_name: checkName,
        // Only repo-ownership repos carry pushed_at; conditionally include it so
        // other repo-based categories don't gain an empty column.
        ...(repo.pushed_at ? { pushed_at: repo.pushed_at } : {}),
      }));

    return {
      checkName,
      passed: allPassedOrNA,
      label: currentCheckStructure.display_name,
      description: currentCheckStructure.description,
      checkDetails,
    };
  });
};
